import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '@shared/providers/AuthContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { AppButton } from '@/components/atoms';
import { convGtJDate } from '@/utils/dateConverter';

/**
 * Tenant Header Props
 */
interface TenantHeaderProps {
  onMenuClick?: () => void;
}

/**
 * Tenant Header Component
 */
export const TenantHeader: React.FC<TenantHeaderProps> = ({ onMenuClick }) => {
  const { user, logout, clinic } = useAuth();
  const { tenant } = useParams<{ tenant: string }>();
  const { theme, colors } = useTheme();
  const siteType = clinic?.site_type || '';
  const isStaging = import.meta.env.VITE_APP_ENV === 'staging';

  const handleLogout = async () => {
    await logout();
  };
  
  return (
    <header
      className="text-white shadow sticky top-0 z-[1100]"
      style={{ backgroundColor: isStaging ? '#000000' : colors.primary }}
    >
      <div className="px-4">
        <div className="relative flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            {/* Menu Toggle Button */}
            <button
              onClick={onMenuClick}
              className="p-2 transition-colors rounded-md hover:bg-white/20"
              data-testid="menu-toggle"
            >
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <Link to={`/${tenant}`} className="text-xl font-bold text-white">
              {theme.displayName}
            </Link>

            {/* Clinic Info - Date, Clinic ID, and Deadline */}
            {clinic && (
              <div className="flex items-center gap-3 px-3 py-1 ml-4 rounded">
                <div className="text-xs leading-relaxed text-white">
                  <div>{clinic.clinic_number}</div>
                  {siteType === 'medix' && clinic.limit_date && (
                    <div>
                      <span>提出期限:</span>
                      <span className="ml-1">{convGtJDate(clinic.limit_date)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {isStaging && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded bg-white px-4 py-1 text-lg font-bold text-black shadow-sm">
              ステージング環境
            </div>
          )}

          {/* User Menu */}
          {user && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-white">{user.name}</span>
              <AppButton
                type="default"
                onClick={handleLogout}
                style={{ backgroundColor: colors.secondary }}
                className="text-sm !text-white px-4 py-1.5 h-auto border border-white/30 hover:!text-white hover:opacity-90"
                data-testid="logout-button"
              >
                ログアウト
              </AppButton>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
